import { HttpErrorResponse } from '@angular/common/http';

import {
  EmailTemplatesAutoReplyItem,
  EmailTemplatesAutoReplyItemTranslation,
} from '@recruitee/http-types';
import { createSingleEntityStore, pipeState, SingleEntityState } from '@recruitee/ngrx';

import { EntityStoreKey } from '../../types';

const { actions, namespace, selectors, reducer } =
  createSingleEntityStore<EmailTemplatesAutoReplyItem>()(
    EntityStoreKey.EmailTemplatesAutoReplyItem,
    {
      // eslint-disable-next-line max-lines-per-function
      customActions: adapter => {
        const save = (
          state: SingleEntityState<EmailTemplatesAutoReplyItem>,
          payload: { data: EmailTemplatesAutoReplyItem },
        ) =>
          pipeState(
            state,
            next => adapter.save(next, payload.data),
            next => adapter.markAsLoaded(next),
          );

        return {
          fetch: (state, payload: number) => adapter.markAsPending(state),
          update: (state, payload: Partial<EmailTemplatesAutoReplyItem>) =>
            adapter.markAsPending(state),
          create: (state, payload: string) => adapter.markAsPending(state),
          createSuccess: (state, payload: { data: EmailTemplatesAutoReplyItem }) =>
            save(state, payload),
          createFailed: (state, payload: HttpErrorResponse) => adapter.setError(state, payload),
          duplicate: (state, payload: number) => adapter.markAsPending(state),
          duplicateSuccess: (state, payload: { data: EmailTemplatesAutoReplyItem }) =>
            pipeState(state, next => adapter.markAsLoaded(next)),
          duplicateFailed: (state, payload: HttpErrorResponse) => adapter.setError(state, payload),
          saveAsTemplate: (
            state,
            payload: {
              title: string;
              offerId: number | string;
              translations: EmailTemplatesAutoReplyItemTranslation[];
            },
          ) => adapter.markAsPending(state),
          saveAsTemplateSuccess: (state, payload: { data: EmailTemplatesAutoReplyItem }) =>
            save(state, payload),
          saveAsTemplateFailed: (state, payload: HttpErrorResponse) =>
            adapter.setError(state, payload),
          loadOfferTemplateItem: (
            state,
            payload: { offerId: number | string; templateId: number },
          ) => adapter.markAsPending(state),
          loadOfferTemplateItemSuccess: (state, payload: { data: EmailTemplatesAutoReplyItem }) =>
            save(state, payload),
          loadOfferTemplateItemFailed: (state, payload: HttpErrorResponse) =>
            adapter.setError(state, payload),
          setDefault: (state, payload: number) => adapter.markAsPending(state),
          setDefaultSuccess: (state, payload: { data: EmailTemplatesAutoReplyItem }) =>
            save(state, payload),
          setDefaultFailed: (state, payload: HttpErrorResponse) => adapter.setError(state, payload),
          deleteTemplate: (state, payload: number) => adapter.markAsPending(state),
          deleteTemplateSuccess: (state, payload: EmailTemplatesAutoReplyItem) =>
            pipeState(
              state,
              next => adapter.reset(),
              next => adapter.markAsLoaded(next),
            ),
          deleteTemplateFailed: (state, payload: HttpErrorResponse) =>
            adapter.setError(state, payload),
          assignTemplateToOffer: (
            state,
            payload: { offerId: number | string; autoReplyTemplateId: number },
          ) => adapter.markAsPending(state),
          assignTemplateToOfferSuccess: (state, payload: { data: EmailTemplatesAutoReplyItem }) =>
            save(state, payload),
          assignTemplateToOfferFailed: (state, payload: HttpErrorResponse) =>
            adapter.setError(state, payload),
          createOrUpdateCustomTemplate: (
            state,
            payload: {
              offerId: number | string;
              translation: { bodyHtml: string; subject: string; langCode: string };
              templateAttachmentAssignments: Array<{ attachmentId: number }>;
              sourceTemplateId: number | null;
              isCustomAssigned: boolean;
            },
          ) => adapter.markAsPending(state),
          createOrUpdateCustomTemplateSuccess: (
            state,
            payload: { data: EmailTemplatesAutoReplyItem },
          ) => save(state, payload),
          createOrUpdateCustomTemplateFailed: (state, payload: HttpErrorResponse) =>
            adapter.setError(state, payload),
        };
      },
    },
  );

export {
  actions as EmailTemplatesAutoReplyItemActions,
  selectors as EmailTemplatesAutoReplyItemSelectors,
  reducer as EmailTemplatesAutoReplyItemReducer,
  namespace as EmailTemplatesAutoReplyItemNamespace,
};
